import { Add, KeyRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Avatar, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { setSearchValue, setSelectedItem } from 'app/actions/token';
import { IconButton, InputSearch, Scrollbar } from 'app/components';
import { TokenDialog, TokenForm, TokenList } from 'app/pages/Token';
import { useAsyncAction } from 'app/services';
import { tokenLoad, tokenUpdate } from 'app/store/token';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const Token = () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const { items, selectedItem, searchValue } = useSelector((state) => state.token);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { asyncAction: fetchData, isLoading: isFetchLoading } = useAsyncAction(tokenLoad);
  const {
    asyncAction: updateSubmit,
    isLoading: isUpdateLoading,
    isSuccessful: isUpdateSuccessful,
    isError: isUpdateError,
  } = useAsyncAction(tokenUpdate, [selectedItem]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleFormClick = () => {
    formRef.current?.handleFormSubmit();
  };

  const handleFormSubmit = (params) => {
    updateSubmit(selectedItem.id, params);
  };

  const handleSearchChange = (value) => dispatch(setSearchValue(value));

  useEffect(() => {
    fetchData();

    return () => dispatch(setSelectedItem(null));
  }, []);

  return (
    <React.Fragment>
      <Stack
        direction='column'
        sx={(theme) => ({
          width: '500px',
          height: '100%',
          zIndex: '1010',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: theme.palette.divider,
        })}>
        {!isFetchLoading && items.length !== 0 && (
          <React.Fragment>
            <Stack
              direction='row'
              alignItems='center'
              sx={(theme) => ({
                padding: theme.spacing(2),
              })}>
              <InputSearch placeholder='Название токена' value={searchValue} onChange={handleSearchChange} />
              <IconButton onClick={handleDialogOpen} endIcon={<Add />} />
              <TokenDialog open={dialogOpen} onClose={handleDialogClose} />
            </Stack>
            <Stack
              direction='column'
              alignItems='center'
              justifyContent='center'
              sx={{
                flex: '1 1 auto',
                overflow: 'hidden',
              }}>
              <TokenList />
            </Stack>
          </React.Fragment>
        )}
        {!isFetchLoading && items.length === 0 && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <Typography align='center' variant='body1' component='div'>
              Здесь пока ничего нет
            </Typography>
            <Typography align='center' color='text.secondary' variant='caption' component='div'>
              В этом списке будут отображаться токены
            </Typography>
          </Stack>
        )}
        {isFetchLoading && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <CircularProgress size={24} />
          </Stack>
        )}
      </Stack>
      <Stack
        direction='column'
        sx={{
          height: '100%',
          flex: '1 1 auto',
        }}>
        {selectedItem && (
          <Scrollbar>
            <Stack
              direction='column'
              alignItems='center'
              sx={(theme) => ({
                maxWidth: '540px',
                margin: 'auto',
                width: '100%',
                padding: theme.spacing(4, 2),
              })}>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  marginBottom: theme.spacing(3),
                  '.MuiAvatar-root': {
                    width: '56px',
                    height: '56px',
                    bgcolor: theme.palette.primary.main,
                  },
                  '.MuiTypography-root:first-of-type': {
                    marginTop: theme.spacing(1),
                  },
                })}>
                <Avatar>
                  <KeyRounded fontSize='large' />
                </Avatar>
                <Typography variant='body1'>{selectedItem.name}</Typography>
              </Stack>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  width: '100%',
                  position: 'relative',
                  padding: theme.spacing(2),
                  border: '1px solid',
                  borderRadius: theme.shape.borderRadius,
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  form: {
                    width: '100%',
                  },
                })}>
                <Stack
                  direction='column'
                  sx={(theme) => ({
                    width: '100%',
                    paddingBottom: theme.spacing(1),
                    '.MuiTypography-h6': {
                      fontSize: '1rem',
                    },
                    '.MuiTypography-caption': {
                      color: theme.palette.text.secondary,
                    },
                  })}>
                  <Typography variant='h6'>Информация</Typography>
                  <Typography variant='caption'>Основная информация токена</Typography>
                </Stack>
                <TokenForm ref={formRef} defaultValues={selectedItem} onSubmit={handleFormSubmit} />
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    '.MuiAlert-root': {
                      width: '100%',
                      marginTop: theme.spacing(2),
                      borderRadius: theme.shape.borderRadius,
                    },
                  })}>
                  {!isUpdateLoading && isUpdateError && (
                    <Alert severity='error'>
                      Произошла ошибка при изменении данных. Попробуйте снова или обратитесь в техническую поддержку.
                    </Alert>
                  )}
                  {!isUpdateLoading && isUpdateSuccessful && <Alert severity='success'>Данные успешно изменены.</Alert>}
                </Stack>
                <Stack
                  direction='row'
                  gap={2}
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    paddingTop: theme.spacing(3),
                  })}>
                  <LoadingButton variant='contained' loading={isUpdateLoading} onClick={handleFormClick} disableElevation>
                    Сохранить изменения
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Scrollbar>
        )}
        {!selectedItem && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={(theme) => ({
              height: '100%',
              padding: theme.spacing(0, 2),
              '.MuiTypography-root': {
                borderRadius: '20px',
                marginTop: '24px',
                padding: theme.spacing(0.5, 2),
                bgcolor: alpha(theme.palette.grey.main, 0.12),
              },
            })}>
            <Typography align='center' color='text.secondary' variant='caption' component='label'>
              Выберите токен из списка
            </Typography>
          </Stack>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default Token;
